import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';

import { KizeoAdminSession } from '@activepieces/shared';

import { adminApi } from './admin-api';

// Mirrors Corp's AdminUserDivision enum (kizeo/shared-models): the session
// carries the raw `corp.users.admin_division` value.
export const ADMIN_DIVISION = {
  DEFAULT: 0,
  DEV: 1,
  MARKETING: 2,
  SUPPORT: 3,
  COM: 4,
  RH: 5,
  PDG: 6,
} as const;

// Paths relative to /internal-admin. Health is the developers' entry point,
// the Explorer the support's; everyone else lands on the Overview.
const LANDING_PATH_BY_DIVISION: Record<number, string> = {
  [ADMIN_DIVISION.DEV]: 'health',
  [ADMIN_DIVISION.SUPPORT]: 'overview',
};
const DEFAULT_LANDING_PATH = 'home';

// Same key as the auth guard, which loads the session before rendering any
// admin page: reading it here never triggers a second request.
export const useAdminSession = () =>
  useQuery<KizeoAdminSession>({
    queryKey: ['internal-admin-session'],
    queryFn: () => adminApi.getSession(),
    staleTime: Infinity,
  });

const DIVISION_LABELS: Record<number, () => string> = {
  [ADMIN_DIVISION.DEV]: () => t('Development'),
  [ADMIN_DIVISION.MARKETING]: () => t('Marketing'),
  [ADMIN_DIVISION.SUPPORT]: () => t('Support'),
  [ADMIN_DIVISION.COM]: () => t('Communication'),
  [ADMIN_DIVISION.RH]: () => t('Human resources'),
  [ADMIN_DIVISION.PDG]: () => t('CEO'),
};

// Null for DEFAULT, an unset division, or a value Corp added since: nothing is
// better than a misleading label.
export const adminDivisionLabel = (division: number | null): string | null =>
  division === null ? null : DIVISION_LABELS[division]?.() ?? null;

export const isDevAdmin = (session: KizeoAdminSession | undefined): boolean =>
  session?.division === ADMIN_DIVISION.DEV;

export const adminLandingPath = (division: number | null): string =>
  (division !== null && LANDING_PATH_BY_DIVISION[division]) ||
  DEFAULT_LANDING_PATH;
